/**
 * The two weeks a project report runs on.
 *
 * Neither is the calendar's. The report and its deck run **Friday to
 * Thursday**, because the weekly meeting is Thursday afternoon and the daily
 * that morning is where the week's tasks are settled — a Monday-to-Sunday
 * report would be read a day after the meeting it is for. The KPI form keeps
 * the ordinary **Monday to Sunday** week, because that is the week its readers
 * outside the team count in.
 *
 * The AI service imposes neither: it takes whatever range it is given, which
 * is why the choice lives here rather than there.
 */

/** `yyyy-MM-dd` in the reader's own calendar, not UTC. */
export function toDateKey(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

/** A range of days, both ends included, as the API takes them. */
export interface ReportWeek {
  weekStart: string
  weekEnd: string
}

/** Sunday is 0 in `Date#getDay`, so Friday is 5 and Monday is 1. */
const FRIDAY = 5
const MONDAY = 1

/**
 * The seven-day run that `date` falls in, starting on the given weekday.
 *
 * @param date     Any day inside the wanted week.
 * @param startDay Weekday the run starts on, in `Date#getDay` numbering.
 * @returns Both ends, inclusive.
 */
function weekFrom(date: Date, startDay: number): ReportWeek {
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  // How far back the last `startDay` was. `+ 7` keeps it positive for every
  // combination; the modulo takes it back under a week.
  const back = (start.getDay() - startDay + 7) % 7
  start.setDate(start.getDate() - back)
  const end = new Date(start)
  end.setDate(start.getDate() + 6)
  return { weekStart: toDateKey(start), weekEnd: toDateKey(end) }
}

/** The Friday-to-Thursday week `date` falls in — the report's and the deck's. */
export function reportWeekOf(date: Date): ReportWeek {
  return weekFrom(date, FRIDAY)
}

/** The Monday-to-Sunday week `date` falls in — the KPI form's. */
export function kpiWeekOf(date: Date): ReportWeek {
  return weekFrom(date, MONDAY)
}

/**
 * The same week, shifted by whole weeks.
 *
 * @param date   The day the current week was derived from.
 * @param offset Weeks to move; negative goes back.
 */
export function shiftWeeks(date: Date, offset: number): Date {
  const moved = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  moved.setDate(moved.getDate() + offset * 7)
  return moved
}

/**
 * A range as a reader reads it: `30/10 – 5/11/2026`.
 *
 * The year is written once, at the end, unless the range crosses into another
 * one — a week that straddles New Year has to say so on both sides.
 */
export function formatWeekRange(week: ReportWeek): string {
  const start = new Date(`${week.weekStart}T00:00:00`)
  const end = new Date(`${week.weekEnd}T00:00:00`)
  const sameYear = start.getFullYear() === end.getFullYear()
  const left = sameYear
    ? `${start.getDate()}/${start.getMonth() + 1}`
    : `${start.getDate()}/${start.getMonth() + 1}/${start.getFullYear()}`
  return `${left} – ${end.getDate()}/${end.getMonth() + 1}/${end.getFullYear()}`
}
